<script setup lang="ts">
import {WheelsSvgIcon} from '../../components';
import { onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue';

import type { IntervalSelectorOption, IntervalSelectorRange, TimeSelectorOption, TimeSelectorRange } from './WheelsTimePicker.types';
import { DEFAULT_TIME_RANGE, DEFAULT_PLACEHOLDER } from './WheelsTimePicker.constants';
import { intervalsBetween, timesBetween } from '../../util/timeStrings';
import type { IntervalSelectorTime, TimeSelectorTime } from '../../util/timeStrings.types';

const props =  withDefaults(defineProps<{
  label?: string;
  selectedTime?: string;
  preselectedTime?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  times?: TimeSelectorOption[];
  timeRange?: TimeSelectorRange;
  intervals?: IntervalSelectorOption[];
  intervalRange?: IntervalSelectorRange;
  rules?: (() => string | boolean)[];
  accentColor?: string;  // This will accept any valid CSS color value
  variant?: 'blue-gray' | 'default';
}>(), {
  variant: 'default'
});

const emit = defineEmits<{
  (e: 'change', value: string): void;
  (e: 'validity-change', value: boolean): void;
}>();

const selectedTime = ref<TimeSelectorTime | IntervalSelectorTime | null | ''>(props.selectedTime as TimeSelectorTime || '');
const preselectedTime = ref<TimeSelectorTime |  IntervalSelectorTime | null | ''>(null)
const timeOptions = ref<TimeSelectorOption[] | IntervalSelectorOption[]>(props.times || props.intervals || []);
const isTimeValid = ref((props.error === false) ? true : false);
const isDropdownOpen = ref(false);
let mode: 'time' | 'interval' = 'time';

const timeSelectorInputElement = ref<HTMLInputElement>();
const timeSelectorOptionsElement = ref<HTMLUListElement>();
const timeSelectorContainer = ref<HTMLDivElement>();
const errorMessages = ref<string[]>([]);

onMounted(() => {

  if (!props.times && !props.intervals) {
    timeOptions.value = timesBetween(DEFAULT_TIME_RANGE.start, DEFAULT_TIME_RANGE.end, DEFAULT_TIME_RANGE.interval).map((time:TimeSelectorTime) => ({
      label: time,
      value: time
    }));
  }

  if (props.timeRange) {
    timeOptions.value = timesBetween(props.timeRange.start, props.timeRange.end, props.timeRange.interval).map((time:TimeSelectorTime) => ({
      label: time,
      value: time
    }));
  }

  if (props.intervalRange) {
    timeOptions.value = intervalsBetween(props.intervalRange.start, props.intervalRange.end, props.intervalRange.interval).map((time:IntervalSelectorTime) => ({
      label: time,
      value: time
    }));
    mode = 'interval';
  }

  if (props.intervals) {
    timeOptions.value = props.intervals.map((interval:IntervalSelectorOption) => ({
      label: interval.label,
      value: interval.value
    }));
    mode = 'interval';
  }

  if (props.selectedTime) {
    preselectTime(props.selectedTime as TimeSelectorTime);
  } else if (props.preselectedTime) {
    preselectTime(props.preselectedTime as TimeSelectorTime);
  }

  document.addEventListener('click', handleClickOutside);

});

onBeforeUnmount(() => {
   document.removeEventListener('click', handleClickOutside);
 });
;

watch(() => props.selectedTime, (newTime) => {
  selectedTime.value = newTime as TimeSelectorTime;
});

watch(isTimeValid, (value) => {
  emit('validity-change', value);
});

watch(isDropdownOpen, (open) => {
  if (open) {
    nextTick(() => {
      if (isDropdownOpen.value) {
        if(selectedTime.value?.length) {
          scrollToValue(selectedTime.value as string);
        } else if (preselectedTime.value?.length) {
          scrollToValue(preselectedTime.value as string);
        }
      }
    });
  }
});

function scrollToValue(value: string): void {
  if (timeSelectorOptionsElement.value &&timeSelectorOptionsElement.value.scrollTo) {
      timeSelectorOptionsElement.value?.scrollTo({
      top: (timeSelectorOptionsElement.value?.querySelector(`[data-value="${value}"]`) as HTMLElement)?.offsetTop - 65 || 0,
      behavior: 'smooth'
    });
  }

}

function selectTime(value: TimeSelectorTime | IntervalSelectorTime):void {
  selectedTime.value = value;
  preselectTime(value);
}

function preselectTime(selection: TimeSelectorTime | IntervalSelectorTime): void {
  preselectedTime.value = selection
}

function promotePreselectedTime (): void {
  if (preselectedTime.value) {
    selectTime(preselectedTime.value);
  }
}

function isPreselectedTime (option: TimeSelectorTime | IntervalSelectorTime): boolean {
  return preselectedTime.value === option
}

function isSelectedTime (option: TimeSelectorTime | IntervalSelectorTime): boolean {
  return selectedTime.value === option
}

function closeDropdown() {
  if (isDropdownOpen.value) {
    isDropdownOpen.value = false;
  }
}


function handleTimeOptionClick(event: MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
  const target = event.currentTarget as HTMLElement;
  const value = target.dataset.value as TimeSelectorTime;

  if (value) {
    selectedTime.value = value;
    preselectedTime.value = value;
    isDropdownOpen.value = false;

    emit('change', value);
  }
}

function handleClickOutside(event: MouseEvent) {
  if (!timeSelectorContainer.value?.contains(event.target as Node)) {
    closeDropdown();
  }
}

const setDropdownMaxHeight = () => {
  if (!timeSelectorOptionsElement.value || !timeSelectorContainer.value) return;
  const inputRect = timeSelectorContainer.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - inputRect.bottom;
  const padding = 100; // px, adjust as needed
  const maxHeight = Math.max(spaceBelow - padding, 150); // 150px min height
  timeSelectorOptionsElement.value.style.maxHeight = `${maxHeight}px`;
}

function handleKeyDown(event: KeyboardEvent) {
  if (!isDropdownOpen.value) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDropdown();
    }
    return;
  }
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    promotePreselectedTime();
    closeDropdown();
  } else if (event.key === 'ArrowDown') {
    event.preventDefault();
    preselectNext(true);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    preselectNext(false);
  } else if (event.key === 'Escape') {
    event.preventDefault();
    closeDropdown();
  }
}

function preselectNext(goForward: boolean): void {
  if (!timeOptions.value || timeOptions.value.length === 0) {
    return;
  }
  if (!preselectedTime.value) {
    preselectTime(timeOptions.value[0].value);
  } else {
    const preselectedIdx = timeOptions.value.findIndex((option) =>
      isPreselectedTime(option.value),
    );
    const newIdx = goForward ? preselectedIdx + 1 : preselectedIdx - 1;
    if (newIdx < 0) {
      preselectTime(timeOptions.value[timeOptions.value.length - 1].value);
    } else if (newIdx >= timeOptions.value.length) {
      preselectTime(timeOptions.value[0].value);
    } else {
      preselectTime(timeOptions.value[newIdx].value);
    }
  }
}

function toggleDropdown() {
  if (!isDropdownOpen.value) {
    setDropdownMaxHeight();
    isDropdownOpen.value = true;
    scrollToValue((selectedTime.value || preselectedTime.value) as string);
  } else {
    isDropdownOpen.value = false;
  }
}

</script>

<template>
  <span
    class="time-selector-wrapper"
    :class="{ 'wheels-form-error': !isTimeValid || props.error, 'wheels-form-disabled': props.disabled }"
  >
  <label
      v-if="props.label"
      for="time-selector-input"
      class="input-label"
      :class="{ 'visually-hidden': !props.label }"
    >
      {{ props.label || DEFAULT_PLACEHOLDER }}
    </label>

    <div
      class="time-selector-input-wrapper wheels-form-field"
      :class="{ 'wheels-form-variant-blue-gray': props.variant === 'blue-gray' }"
      role="combobox"
      :aria-expanded="isDropdownOpen"
      :aria-haspopup="true"
      :aria-controls="'time-selector-options'"
      :aria-invalid="!isTimeValid"
      :aria-disabled="props.disabled"
      ref="timeSelectorContainer"
      @click="toggleDropdown"
      @keydown="handleKeyDown"
    >

      <WheelsSvgIcon
        icon="clock"
        class="time-selector-icon time-selector-icon--left"
        aria-hidden="true"
      />
      <input
        ref="timeSelectorInputElement"
        type="text"
        class="time-selector-input"
        :value="selectedTime"
        autocomplete="off"
        readonly
        :placeholder="props.placeholder || DEFAULT_PLACEHOLDER"
        :disabled="props.disabled"
        :aria-label="props.placeholder || 'Select time'"
        :aria-activedescendant="preselectedTime ? `option-${preselectedTime}` : undefined"
        style="border: none !important"
      />
      <span
        class="time-selector-norgie"
        :aria-label="'Toggle time options'"
        :aria-expanded="isDropdownOpen"
        role="button"
      >
        <WheelsSvgIcon icon="norgie" aria-hidden="true" />
      </span>
      <ul
        v-if="isDropdownOpen"
        class="wheels-form-field__dropdown"
        :class="{ 'active': isDropdownOpen }"
        :data-open="isDropdownOpen"
        ref="timeSelectorOptionsElement"
        role="listbox"
        :aria-label="mode === 'time' ? 'Time options' : 'Interval options'"
      >
        <li
          v-for="timeOption in timeOptions"
          :key="timeOption.value"
          class="wheels-form-field__dropdown-item"
          :class="{
            'wheels-form-field__dropdown-item--preselected': isPreselectedTime(timeOption.value),
            'wheels-form-field__dropdown-item--selected': isSelectedTime(timeOption.value)
          }"
          :data-value="timeOption.value"
          role="option"
          :aria-selected="isPreselectedTime(timeOption.value)"
          @click="handleTimeOptionClick"
        >
          <span v-if="timeOption.title" class="time-option-title">{{ timeOption.title }}</span>
          <span class="time-option-label">{{ timeOption.label }}</span>
        </li>
      </ul>

    </div>
    <ul v-if="!isTimeValid" class="time-selector-error-messages">
      <li v-for="errorMessage in errorMessages" :key="errorMessage">{{ errorMessage }}</li>
    </ul>
  </span>
</template>

<style scoped lang="scss">
.wheels-form-field__dropdown {
  &:not(:hover) {
    .wheels-form-field__dropdown-item--preselected {
      background-color: var(--cu-gray-bg);
      border-radius: 4px;
    }
  }
  &:hover {
    .wheels-form-field__dropdown-item--preselected {
      background-color: transparent;
    }
  }

}

.wheels-form-field__dropdown-item {
  padding: var(--spacing-3) var(--spacing-6);
  cursor: pointer;
  align-items: center;
  justify-content: space-between;
  border-left: 3px solid white;
  border-right: 3px solid white;

  &:hover {
    background-color: var(--cu-gray-bg);
    border-radius: 4px;
  }

  &--selected {
    background-color: var(--cu-gray-bg);
    border-radius: 4px;
  }
}

.time-selector-wrapper {
  --form-field-accent-color: v-bind('props.accentColor || "var(--cu-blue-primary)"');
}

.time-selector-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  .time-selector-norgie, .time-selector-icon, .time-selector-input-wrapper {
    transition: color .1s ease, border-color .1s ease;
  }
}

.time-selector-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: var(--cu-gray-400);
  cursor: pointer;
}

.time-selector-norgie {
  display: inline-block;
  width: var(--spacing-5);
  height: var(--spacing-5);
  background-color: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: var(--cu-gray-400);
}

.time-selector-options {
  position: absolute;
  top: calc(100% + 3px);
  left: 0;
  right: 0;
  border-radius: 6px;
  background-color: #fff;
  border-radius: 6px;
  z-index: 1000;
  margin: 0;
  padding: 0;
  list-style: none;
  display: none;
  opacity: 0.5;
  transform: scale(0.85);
  padding-bottom: 16px;
  overflow-y: auto;
  box-shadow: var(--cu-shadow-900);
  transition: opacity .15s ease,
  transform .15s ease,
  display .15s ease allow-discrete;
}

.time-selector-options.active {
  opacity: 1;
  display: block;
  transform: scale(1);
}

@starting-style {
  .time-selector-options.active {
    opacity: 0.5;
    transform: scale(0.85);
  }
}



.time-option-title {
  color: var(--cu-gray-700);
  display: block;
  font-size: .8rem;
  margin-bottom: .2rem;
  font-weight: 600;
}

.time-option-label {
  color: var(--cu-gray-700);
}

.time-selector-error-messages {
  color: var(--cu-error-primary);
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: .8rem;
  margin-top: .2rem;
}

</style>
